import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccountService } from './account.service';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const DARK_THEME_CLASS = 'dark-theme';
// Plage horaire (heure locale) pendant laquelle le thème automatique est clair
const DAY_START_HOUR = 7;
const NIGHT_START_HOUR = 19;
const AUTO_CHECK_INTERVAL_MS = 60 * 1000;

// Thème de l'interface : automatique selon l'heure (clair le jour, sombre la nuit) par défaut,
// sinon celui choisi dans Paramètres → Préférences
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private theme = new BehaviorSubject<Theme>('light');
  theme$ = this.theme.asObservable();

  // Choix explicite de l'utilisateur ; null = automatique selon l'heure
  private choice: Theme | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    accountService: AccountService
  ) {
    // Thème enregistré sur le compte : retrouvé d'un appareil à l'autre
    accountService.account$.subscribe(account => {
      const saved = asTheme(account?.preferences?.['theme'] as string);
      if (saved && saved !== this.choice) {
        this.setTheme(saved);
      }
    });
  }

  // Appelé avant le premier rendu (provideAppInitializer) pour éviter un flash dans le mauvais thème
  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.choice = asTheme(this.readStorage());
    this.apply(this.choice || this.automaticTheme);
    // En mode automatique, bascule d'elle-même au lever / à la tombée du jour
    this.zone.runOutsideAngular(() => setInterval(() => {
      if (!this.choice && this.automaticTheme !== this.current) {
        this.zone.run(() => this.apply(this.automaticTheme));
      }
    }, AUTO_CHECK_INTERVAL_MS));
  }

  get current(): Theme {
    return this.theme.value;
  }

  get userChoice(): Theme | null {
    return this.choice;
  }

  get automaticTheme(): Theme {
    const hour = new Date().getHours();
    return hour >= DAY_START_HOUR && hour < NIGHT_START_HOUR ? 'light' : 'dark';
  }

  // null = revenir au thème automatique
  setTheme(theme: Theme | null): void {
    this.choice = theme;
    this.writeStorage(theme);
    this.apply(theme || this.automaticTheme);
  }

  private apply(theme: Theme): void {
    this.document.documentElement.classList.toggle(DARK_THEME_CLASS, theme === 'dark');
    this.theme.next(theme);
  }

  private readStorage(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeStorage(theme: Theme | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      if (theme) {
        localStorage.setItem(STORAGE_KEY, theme);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Stockage indisponible (navigation privée…) : le choix ne vaut que pour la session
    }
  }
}

function asTheme(value: string | null | undefined): Theme | null {
  return value === 'light' || value === 'dark' ? value : null;
}
